import { aiSpawnSpecies, type AiSpawnSpecies } from "./ai-spawn.js";
import { GATEWAY_MAP_SIZE, gatewayMapToWorld } from "./gateway-map.js";

export const ediblePlantSpecies = [
  // Keep the first three IDs stable so already-saved owner plans migrate to
  // their real current-build edible Blueprint equivalents without recreation.
  { id: "fern", name: "Fiddlehead", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Fiddlehead.BP_Fiddlehead_C" },
  { id: "cycad", name: "Agave", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Agave.BP_Agave_C" },
  { id: "edible_mushroom", name: "Chanterelle mushroom", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_ChanterelleMushroom.BP_ChanterelleMushroom_C" },
  { id: "crimson_k_trifolium", name: "Crimson Apollan K-Trifolium", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_CrimsonApollanKTrifolium01.BP_CrimsonApollanKTrifolium01_C" },
  { id: "azure_k_trifolium", name: "Azure Apollan K-Trifolium", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_AzureApollanKTrifolium01.BP_AzureApollanKTrifolium01_C" },
  { id: "violet_k_trifolium", name: "Violet Apollan K-Trifolium", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_VioletApollanKTrifolium.BP_VioletApollanKTrifolium_C" },
  { id: "sunchoke_roots", name: "Sunchoke roots", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_SunchokeRoots.BP_SunchokeRoots_C" },
  { id: "clintonia", name: "Clintonia", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Clintonia.BP_Clintonia_C" },
  { id: "fireweed", name: "Fireweed", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Fireweed.BP_Fireweed_C" },
  { id: "marigold", name: "Marigold", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Marigold.BP_Marigold_C" },
  { id: "red_currant", name: "Red currant", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_RedCurrant.BP_RedCurrant_C" },
  { id: "russula", name: "Russula mushroom", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Russula.BP_Russula_C" },
  { id: "sumac", name: "Sumac", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Sumac.BP_Sumac_C" },
  { id: "trillium", name: "Trillium", classPath: "/Game/TheIsle/Core/Spawnables/EdiblePlants/BP_Trillium.BP_Trillium_C" }
] as const;

export type EdiblePlantSpeciesId = (typeof ediblePlantSpecies)[number]["id"];

export interface EcosystemZoneSpeciesWeight<TSpecies extends string> {
  species: TSpecies;
  weight: number;
}

export interface EcosystemZoneCenter {
  mapX: number;
  mapY: number;
}

export interface AiSpawnZone {
  id: string;
  name: string;
  category: string;
  enabled: boolean;
  center: EcosystemZoneCenter;
  radiusWorldUnits: number;
  spawnRangeWorldUnits: number;
  countPerWave: number;
  growth: number;
  respawnSeconds: number;
  despawnSecondsWithoutPlayers: number;
  maxAlive: number;
  behavior: "native" | "aggressive";
  primeQuestPolicy: "neutral" | "exclude";
  markerColor: string;
  species: Array<EcosystemZoneSpeciesWeight<AiSpawnSpecies>>;
}

export interface PlantSpawnZone {
  id: string;
  name: string;
  enabled: boolean;
  center: EcosystemZoneCenter;
  radiusWorldUnits: number;
  countPerRestart: number;
  species: Array<EcosystemZoneSpeciesWeight<EdiblePlantSpeciesId>>;
}

export interface EcosystemZoneConfig {
  version: 1;
  aiExecution: "off" | "test";
  plantExecution: "off" | "test";
  aiZones: AiSpawnZone[];
  plantZones: PlantSpawnZone[];
}

export interface PlantTestSeed {
  id: string;
  zoneId: string;
  species: EdiblePlantSpeciesId;
  targetX: number;
  targetY: number;
}

export interface PlantTestSeedLimits {
  maxZones: number;
  maxPerZone: number;
  maxTotal: number;
}

export const LIVE_PLANT_ZONE_LIMITS: PlantTestSeedLimits = {
  maxZones: 8,
  maxPerZone: 100,
  maxTotal: 200
};

export interface AiZoneTestSeed {
  id: string;
  zoneId: string;
  species: AiSpawnSpecies;
  targetX: number;
  targetY: number;
  centerX: number;
  centerY: number;
  activationRadiusWorldUnits: number;
  growth: number;
  behavior: AiSpawnZone["behavior"];
  countPerWave: number;
  respawnSeconds: number;
  requestedDespawnSecondsWithoutPlayers: number;
}

export interface AiZoneTestSeedLimits {
  maxZones: number;
  maxPerZone: number;
  maxTotal: number;
}

export const LIVE_AI_ZONE_LIMITS: AiZoneTestSeedLimits = {
  maxZones: 10,
  maxPerZone: 20,
  maxTotal: 200
};

/**
 * Species proven safe in the exact weak-identity native lifecycle registry.
 * Owner configuration may retain future species, but a live Director plan must
 * never compile a species that the native side cannot own and retire safely.
 */
export const exactNativeAiDirectorSpecies = [
  "Carnotaurus",
  "Dryosaurus",
  "Diabloceratops",
  "Gallimimus",
  "Omniraptor",
  "Tenontosaurus"
] as const satisfies readonly AiSpawnSpecies[];

const exactNativeAiDirectorSpeciesSet = new Set<AiSpawnSpecies>(exactNativeAiDirectorSpecies);

export const defaultEcosystemZoneConfig: EcosystemZoneConfig = {
  version: 1,
  aiExecution: "off",
  plantExecution: "off",
  aiZones: [],
  plantZones: []
};

export function validateEcosystemZoneConfig(input: EcosystemZoneConfig): EcosystemZoneConfig {
  if (input.version !== 1) throw new Error("Ecosystem zone configuration version is unsupported.");
  if (!["off", "test"].includes(input.aiExecution)) throw new Error("AI zone execution mode is invalid.");
  if (!["off", "test"].includes(input.plantExecution)) throw new Error("Plant zone execution mode is invalid.");
  if (input.aiZones.length > 12) throw new Error("At most 12 AI spawn zones are allowed.");
  if (input.plantZones.length > 8) throw new Error("At most 8 plant spawn zones are allowed.");

  const ids = [...input.aiZones, ...input.plantZones].map((zone) => zone.id);
  if (new Set(ids).size !== ids.length) throw new Error("Ecosystem zone IDs must be unique.");

  const aiZones = input.aiZones.map((zone) => validateAiZone(zone));
  const plantZones = input.plantZones.map((zone) => validatePlantZone(zone));
  const enabledPlantTotal = plantZones
    .filter((zone) => zone.enabled)
    .reduce((total, zone) => total + zone.countPerRestart, 0);
  if (enabledPlantTotal > LIVE_PLANT_ZONE_LIMITS.maxTotal) {
    throw new Error(`Enabled plant zones allow at most ${LIVE_PLANT_ZONE_LIMITS.maxTotal} live plants in total.`);
  }

  return {
    version: 1,
    aiExecution: input.aiExecution,
    plantExecution: input.plantExecution,
    aiZones,
    plantZones
  };
}

/**
 * Produces a deterministic, restart-bounded seed plan for an explicitly
 * coordinated game-server plant test. It does not enable execution.
 */
export function createPlantTestSeedPlan(
  input: EcosystemZoneConfig,
  limits: PlantTestSeedLimits = { maxZones: 2, maxPerZone: 20, maxTotal: 40 }
): PlantTestSeed[] {
  return compilePlantSeedPlan(input, limits, false);
}

/**
 * Produces the owner-controlled live plant population plan. Slot positions and
 * weighted species choices are prefix-stable, so changing a zone count adds or
 * removes only the difference instead of reshuffling every existing plant.
 */
export function createLivePlantSeedPlan(input: EcosystemZoneConfig): PlantTestSeed[] {
  return compilePlantSeedPlan(input, LIVE_PLANT_ZONE_LIMITS, true);
}

function compilePlantSeedPlan(
  input: EcosystemZoneConfig,
  limits: PlantTestSeedLimits,
  allowEmpty: boolean
): PlantTestSeed[] {
  const config = validateEcosystemZoneConfig(input);
  const zones = config.plantZones.filter((zone) => zone.enabled);
  assertIntegerRange(limits.maxZones, 1, 8, "Plant test maximum zones");
  assertIntegerRange(limits.maxPerZone, 1, LIVE_PLANT_ZONE_LIMITS.maxPerZone, "Plant test maximum per zone");
  assertIntegerRange(limits.maxTotal, 1, 240, "Plant test maximum total");
  if (zones.length === 0 && !allowEmpty) throw new Error("Plant test requires at least one enabled plant zone.");
  if (zones.length > limits.maxZones) {
    throw new Error(`Plant test allows at most ${limits.maxZones} enabled plant zones.`);
  }

  const total = zones.reduce((sum, zone) => sum + zone.countPerRestart, 0);
  if (zones.some((zone) => zone.countPerRestart > limits.maxPerZone)) {
    throw new Error(`Plant test allows at most ${limits.maxPerZone} plants per zone.`);
  }
  if (total > limits.maxTotal) throw new Error(`Plant test allows at most ${limits.maxTotal} plants per restart.`);

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const seeds: PlantTestSeed[] = [];
  for (const zone of zones) {
    const center = gatewayMapToWorld(zone.center.mapX, zone.center.mapY);
    const rotation = stableUnitInterval(zone.id) * Math.PI * 2;
    const speciesPlan = allocatePlantSpecies(zone.species, zone.countPerRestart);
    for (let index = 0; index < zone.countPerRestart; index += 1) {
      const angle = rotation + (index * goldenAngle);
      const radius = zone.radiusWorldUnits * 0.9
        * Math.sqrt(0.05 + (stableUnitInterval(`${zone.id}:plant-radius:${index}`) * 0.95));
      seeds.push({
        id: `${zone.id}-${String(index + 1).padStart(2, "0")}`,
        zoneId: zone.id,
        species: speciesPlan[index]!,
        targetX: Math.round((center.worldX + (Math.cos(angle) * radius)) * 100) / 100,
        targetY: Math.round((center.worldY + (Math.sin(angle) * radius)) * 100) / 100
      });
    }
  }
  return seeds;
}

/**
 * Compiles the owner-authored AI territory plan into deterministic spawn slots.
 * The game-side test executor may refill an absent slot but never destroys AI;
 * a coordinated server restart remains the cleanup boundary.
 */
export function createAiZoneTestSeedPlan(
  input: EcosystemZoneConfig,
  limits: AiZoneTestSeedLimits = LIVE_AI_ZONE_LIMITS
): AiZoneTestSeed[] {
  const config = validateEcosystemZoneConfig(input);
  const zones = config.aiZones.filter((zone) => zone.enabled);
  assertIntegerRange(limits.maxZones, 1, 12, "AI zone test maximum zones");
  assertIntegerRange(limits.maxPerZone, 1, 20, "AI zone test maximum per zone");
  assertIntegerRange(limits.maxTotal, 1, 240, "AI zone test maximum total");
  if (zones.length === 0) throw new Error("AI zone test requires at least one enabled territory.");
  if (zones.length > limits.maxZones) throw new Error(`AI zone test allows at most ${limits.maxZones} enabled territory.`);
  if (zones.some((zone) => zone.maxAlive > limits.maxPerZone)) {
    throw new Error(`AI zone test allows at most ${limits.maxPerZone} living AI per territory.`);
  }
  const total = zones.reduce((sum, zone) => sum + zone.maxAlive, 0);
  if (total > limits.maxTotal) throw new Error(`AI zone test allows at most ${limits.maxTotal} living AI in total.`);

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const seeds: AiZoneTestSeed[] = [];
  for (const zone of zones) {
    const nativeSpecies = zone.species.filter((entry) => exactNativeAiDirectorSpeciesSet.has(entry.species));
    if (nativeSpecies.length === 0) {
      throw new Error(`AI territory ${zone.id} has no species enabled for the exact native rollout.`);
    }
    const center = gatewayMapToWorld(zone.center.mapX, zone.center.mapY);
    const rotation = stableUnitInterval(zone.id) * Math.PI * 2;
    const totalWeight = nativeSpecies.reduce((sum, entry) => sum + entry.weight, 0);
    for (let index = 0; index < zone.maxAlive; index += 1) {
      const angle = rotation + (index * goldenAngle);
      const radius = zone.spawnRangeWorldUnits * 0.9 * Math.sqrt((index + 0.5) / zone.maxAlive);
      const sample = ((index * 0.6180339887498949) + stableUnitInterval(`${zone.id}:ai:${index}`)) % 1;
      seeds.push({
        id: `${zone.id}-${String(index + 1).padStart(2, "0")}`,
        zoneId: zone.id,
        species: weightedSpecies(nativeSpecies, sample * totalWeight),
        targetX: roundWorld(center.worldX + (Math.cos(angle) * radius)),
        targetY: roundWorld(center.worldY + (Math.sin(angle) * radius)),
        centerX: roundWorld(center.worldX),
        centerY: roundWorld(center.worldY),
        activationRadiusWorldUnits: zone.radiusWorldUnits,
        growth: zone.growth,
        behavior: zone.behavior,
        countPerWave: zone.countPerWave,
        respawnSeconds: zone.respawnSeconds,
        requestedDespawnSecondsWithoutPlayers: zone.despawnSecondsWithoutPlayers
      });
    }
  }
  return seeds;
}

function validateAiZone(zone: AiSpawnZone): AiSpawnZone {
  validateZoneIdentity(zone);
  validateCenter(zone.center);
  if (zone.category.trim().length > 40) throw new Error("AI zone category must use at most 40 characters.");
  assertIntegerRange(zone.radiusWorldUnits, 1_000, 50_000, "AI zone radius");
  assertIntegerRange(zone.spawnRangeWorldUnits, 500, zone.radiusWorldUnits, "AI spawn range");
  assertIntegerRange(zone.countPerWave, 1, 5, "AI count per wave");
  assertNumberRange(zone.growth, 0.05, 1, "AI growth");
  assertIntegerRange(zone.respawnSeconds, 60, 3_600, "AI respawn interval");
  assertIntegerRange(zone.despawnSecondsWithoutPlayers, 30, 3_600, "AI no-player despawn delay");
  assertIntegerRange(zone.maxAlive, zone.countPerWave, 20, "AI maximum alive");
  if (!["native", "aggressive"].includes(zone.behavior)) throw new Error("AI zone behavior is invalid.");
  if (!["neutral", "exclude"].includes(zone.primeQuestPolicy)) throw new Error("AI Prime quest policy is invalid.");
  if (!/^#[0-9a-f]{6}$/i.test(zone.markerColor)) throw new Error("AI marker colour must be a six-digit hex colour.");
  return {
    ...zone,
    name: zone.name.trim(),
    category: zone.category.trim(),
    center: { ...zone.center },
    growth: Math.round(zone.growth * 100) / 100,
    markerColor: zone.markerColor.toUpperCase(),
    species: validateWeights(zone.species, new Set<string>(aiSpawnSpecies), 8, "AI")
  };
}

function validatePlantZone(zone: PlantSpawnZone): PlantSpawnZone {
  validateZoneIdentity(zone);
  validateCenter(zone.center);
  assertIntegerRange(zone.radiusWorldUnits, 500, 30_000, "Plant zone radius");
  assertIntegerRange(zone.countPerRestart, 1, LIVE_PLANT_ZONE_LIMITS.maxPerZone, "Live plant target");
  return {
    ...zone,
    name: zone.name.trim(),
    center: { ...zone.center },
    species: validateWeights(zone.species, new Set<string>(ediblePlantSpecies.map((plant) => plant.id)), 8, "Plant")
  };
}

function validateZoneIdentity(zone: { id: string; name: string; enabled: boolean }): void {
  if (!/^[a-z0-9][a-z0-9_-]{2,39}$/.test(zone.id)) throw new Error("Zone ID must use 3-40 lowercase letters, numbers, hyphens, or underscores.");
  if (zone.name.trim().length < 3 || zone.name.trim().length > 60) throw new Error("Zone name must use 3-60 characters.");
  if (typeof zone.enabled !== "boolean") throw new Error("Zone enabled state must be true or false.");
}

function validateCenter(center: EcosystemZoneCenter): void {
  assertNumberRange(center.mapX, 0, GATEWAY_MAP_SIZE, "Zone map X");
  assertNumberRange(center.mapY, 0, GATEWAY_MAP_SIZE, "Zone map Y");
}

function validateWeights<TSpecies extends string>(
  values: Array<EcosystemZoneSpeciesWeight<TSpecies>>,
  allowed: Set<string>,
  maximum: number,
  label: string
): Array<EcosystemZoneSpeciesWeight<TSpecies>> {
  if (values.length < 1 || values.length > maximum) throw new Error(`${label} zones require between 1 and ${maximum} weighted species.`);
  const normalized = values.map((entry) => {
    if (!allowed.has(entry.species)) throw new Error(`${label} zone species ${entry.species} is unsupported.`);
    assertIntegerRange(entry.weight, 1, 100, `${label} species weight`);
    return { species: entry.species, weight: entry.weight };
  });
  if (new Set(normalized.map((entry) => entry.species)).size !== normalized.length) {
    throw new Error(`${label} zone species must be unique.`);
  }
  return normalized;
}

function assertIntegerRange(value: number, min: number, max: number, label: string): void {
  if (!Number.isInteger(value) || value < min || value > max) throw new Error(`${label} must be a whole number from ${min} to ${max}.`);
}

function assertNumberRange(value: number, min: number, max: number, label: string): void {
  if (!Number.isFinite(value) || value < min || value > max) throw new Error(`${label} must be from ${min} to ${max}.`);
}

function allocatePlantSpecies(
  values: Array<EcosystemZoneSpeciesWeight<EdiblePlantSpeciesId>>,
  count: number
): EdiblePlantSpeciesId[] {
  const totalWeight = values.reduce((sum, entry) => sum + entry.weight, 0);
  const scores = values.map(() => 0);
  const result: EdiblePlantSpeciesId[] = [];
  for (let slot = 0; slot < count; slot += 1) {
    for (let index = 0; index < values.length; index += 1) scores[index] += values[index]!.weight;
    let selected = 0;
    for (let index = 1; index < values.length; index += 1) {
      if (scores[index]! > scores[selected]!) selected = index;
    }
    scores[selected] -= totalWeight;
    result.push(values[selected]!.species);
  }
  return result;
}

function weightedSpecies(
  values: Array<EcosystemZoneSpeciesWeight<AiSpawnSpecies>>,
  sample: number
): AiSpawnSpecies {
  let cursor = 0;
  for (const entry of values) {
    cursor += entry.weight;
    if (sample < cursor) return entry.species;
  }
  return values.at(-1)!.species;
}

function roundWorld(value: number): number {
  return Math.round(value * 100) / 100;
}

function stableUnitInterval(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0) / 4_294_967_296;
}
